export interface VoicePlanCategory {
  id: string;
  name: string;
  tags: string[];
  /** How tags are matched: 'and' (all must match, default) or 'or' (any must match). */
  matchMode?: 'and' | 'or';
}

const ROLE_TAG_MAP: Record<string, string> = {
  teacher: 'my-students',
  student: 'my-teachers',
  both: 'my-voice-plans',
};

export class VoicePlan {
    id: string;
    name: string;
    timestamp: string;
    /** @deprecated Use tags + canEdit instead. Legacy field kept for backward compatibility. */
    role: string;
    trashed: boolean;
    private _tags: string[] = [];
    canEdit: boolean;
    invited?: boolean;
    accepted?: boolean;
    invitedByEmail?: string;
    invitedByName?: string;

    constructor(
      id: string,
      name: string,
      timestamp: string,
      role: string,
      trashed: boolean,
      tags: string[] = [],
      canEdit = false,
      invited?: boolean,
      accepted?: boolean,
      invitedByEmail?: string,
      invitedByName?: string
    ) {
        this.id = id;
        this.name = name ?? '';
        this.timestamp = timestamp;
        this.role = role;
        this.trashed = trashed;
        this._tags = (tags ?? []).filter((t: any) => typeof t === 'string');
        this.canEdit = canEdit;
        this.invited = invited;
        this.accepted = accepted;
        this.invitedByEmail = invitedByEmail;
        this.invitedByName = invitedByName;
    }

    get tags(): string[] {
      const implicit: string[] = [];
      if (this.role && ROLE_TAG_MAP[this.role]) {
        implicit.push(ROLE_TAG_MAP[this.role]);
      }
      if (this.invited && this.accepted !== true) {
        implicit.push('invited');
      }
      const unique = new Set([...(this._tags ?? []), ...implicit]);
      const result = Array.from(unique);
      // Strip stale 'invited' from stored _tags when the invitation has been accepted
      if (this.accepted === true) {
        return result.filter(t => t !== 'invited');
      }
      return result;
    }

    set tags(val: string[]) {
      const implicit: string[] = [];
      if (this.role && ROLE_TAG_MAP[this.role]) {
        implicit.push(ROLE_TAG_MAP[this.role]);
      }
      if (this.invited && this.accepted !== true) {
        implicit.push('invited');
      }
      this._tags = (val ?? []).filter(t => !implicit.includes(t) && !(t === 'invited' && this.accepted === true));
    }

    /**
     * Returns all tags for this voice plan, including implicit tags
     * derived from the legacy `role` field for backward compatibility.
     * @deprecated Use `tags` property instead.
     */
    getTags(): string[] {
      return this.tags;
    }

    static fromJson(json: any) {
        const isTeacher = json.role === 'teacher' || json.role === 'both';
        return new VoicePlan(
          json.id,
          json.name ?? '',
          json.timestamp,
          json.role ?? '',
          json.trashed ?? false,
          Array.isArray(json.tags) ? json.tags.filter((t: any) => typeof t === 'string') : [],
          isTeacher || (json.canEdit ?? false),
          json.invited,
          json.accepted,
          json.invitedByEmail,
          json.invitedByName,
        );
    }

    toJson(): any {
        const data: any = {
            id: this.id,
            name: this.name,
            timestamp: this.timestamp,
            role: this.role ?? '',
            trashed: this.trashed ?? false,
            tags: this._tags ?? [],
            canEdit: this.canEdit ?? false,
        };
        if (this.invited !== undefined) {
            data.invited = this.invited;
        }
        if (this.accepted !== undefined) {
            data.accepted = this.accepted;
        }
        if (this.invitedByEmail !== undefined) {
            data.invitedByEmail = this.invitedByEmail;
        }
        if (this.invitedByName !== undefined) {
            data.invitedByName = this.invitedByName;
        }
        return data;
    }
}

/**
 * Default voice plan categories seeded for new users.
 */
export const DEFAULT_VOICE_PLAN_CATEGORIES: VoicePlanCategory[] = [
  { id: 'my-students', name: 'My Students', tags: ['my-students'] },
  { id: 'my-teachers', name: 'My Teachers', tags: ['my-teachers'] },
  { id: 'my-voice-plans', name: 'My Voice Plans', tags: ['my-voice-plans'] },
];
